import { useQuery } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { authService } from '~/services/authAPI';
import { EnhancedRealmMetadata, getRealmFallbackData } from '~/utils/cloudHelper';
import { formatErrorPayload, validateApiResponse } from './utils';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
enum Errors {
  GET_REALM_METADATA_ERROR = 'GET_REALM_METADATA_ERROR',
}

export const getRealmMetadataQueryKey = ({ hostname }: Pick<Location, 'hostname'>) => [
  'realmMetadata',
  hostname,
];

/**
 * Fetches realm metadata if it is determined that we are in a virtual room, returns:
 * - metadata and isVirtualRoom = true flag if in virtual room
 * - placeholder data and isVirtualRoom = false flag if not in virtual room
 *
 * query will not fetch data and will return placeholder data instead if not in a virtual room
 */
export const useRealmMetadataQuery = (
  { origin, hostname }: Pick<Location, 'origin' | 'host' | 'hostname'> = window.location,
) => {
  const dispatch = useDispatch();
  const placeholderData = getRealmFallbackData({ hostname, origin });
  const enabled = placeholderData.isSSO && !placeholderData.isLocalhost;

  return useQuery<EnhancedRealmMetadata, Error>(
    getRealmMetadataQueryKey({ hostname }),
    async () => {
      const response = validateApiResponse(await authService.getRealmMetadata({ origin }));
      return {
        ...placeholderData,
        ...response.data,
      };
    },
    {
      onError: (error: Error) =>
        dispatch(
          formatErrorPayload(error, {
            // type: Errors.GET_REALM_METADATA_ERROR,
            hostname,
          }),
        ),
      placeholderData,
      cacheTime: Infinity,
      enabled,
    },
  );
};
